import { queryOptions, skipToken } from "@tanstack/react-query";

import { createInfiniteQueryOptions } from "@/lib/query";
import {
	$getContentVersion,
	$getContentVersionAccess,
	$getOrCreateVersionAttempt,
	$listUserContentVersions,
	$searchUserMistakes,
	$listPublicVersions,
	$listContentVersions,
	$listVersionAttempts,
} from "@/server/actions/content";
import {
	$adminListThreads,
	$getThread,
	$isThreadStreaming,
	$listFeaturedThreads,
	$listThreads,
} from "@/server/actions/thread";

export const isThreadStreamingOptions = (threadId: string) =>
	queryOptions({
		queryKey: ["threads", "isStreaming", threadId],
		queryFn: () => $isThreadStreaming({ data: { threadId } }),
		select: (data) => data.data,
	});

export const threadQueryOptions = ({
	threadId,
	enabled,
}: {
	threadId?: string;
	enabled?: boolean;
}) =>
	queryOptions({
		queryKey: ["threads", threadId],
		queryFn: threadId ? () => $getThread({ data: { threadId } }) : skipToken,
		enabled,
	});

export const suspenseThreadQueryOptions = ({ threadId }: { threadId: string }) =>
	queryOptions({
		queryKey: ["threads", threadId],
		queryFn: () => $getThread({ data: { threadId } }),
	});

export const threadsInfiniteQueryOptions = ({
	pageSize = 10,
	enabled,
}: {
	pageSize?: number;
	enabled?: boolean;
} = {}) =>
	createInfiniteQueryOptions({
		queryKey: ["threads", "list", { pageSize }],
		queryFn: ({ pageParam }) => $listThreads({ data: { page: pageParam ?? 1, pageSize } }),
		enabled,
	});

export const featuredThreadsInfiniteQueryOptions = ({
	pageSize = 10,
	enabled,
}: {
	pageSize?: number;
	enabled?: boolean;
} = {}) =>
	createInfiniteQueryOptions({
		queryKey: ["threads", "featured", { pageSize }],
		queryFn: ({ pageParam }) =>
			$listFeaturedThreads({ data: { page: pageParam ?? 1, pageSize } }),
		enabled,
	});

export const adminThreadsInfiniteQueryOptions = ({
	pageSize = 10,
	search,
	enabled,
}: {
	pageSize?: number;
	search?: string;
	enabled?: boolean;
} = {}) =>
	createInfiniteQueryOptions({
		queryKey: ["threads", "admin", { pageSize, search }],
		queryFn: ({ pageParam }) =>
			$adminListThreads({
				data: { page: pageParam ?? 1, pageSize, search },
			}),
		enabled,
	});

export const versionQueryOptions = ({ versionId }: { versionId: string }) =>
	queryOptions({
		queryKey: ["versions", versionId],
		queryFn: () => $getContentVersionAccess({ data: { versionId } }),
	});

export const contentVersionQueryOptions = ({ versionId }: { versionId: string }) =>
	queryOptions({
		queryKey: ["versions", versionId, "detail"],
		queryFn: () => $getContentVersion({ data: { versionId } }),
	});

export const versionAttemptQueryOptions = ({
	versionId,
	enabled,
}: {
	versionId: string;
	enabled?: boolean;
}) =>
	queryOptions({
		queryKey: ["version-attempts", versionId],
		queryFn: () => $getOrCreateVersionAttempt({ data: { versionId } }),
		enabled,
	});

export const versionsListQueryOptions = ({ threadId }: { threadId: string }) =>
	queryOptions({
		queryKey: ["versions", "list", threadId],
		queryFn: () => $listContentVersions({ data: { threadId } }),
	});

export const userVersionsInfiniteQueryOptions = ({
	pageSize = 20,
	enabled,
}: {
	pageSize?: number;
	enabled?: boolean;
} = {}) =>
	createInfiniteQueryOptions({
		queryKey: ["versions", "mine", { pageSize }],
		queryFn: ({ pageParam }) =>
			$listUserContentVersions({
				data: { page: pageParam ?? 1, pageSize },
			}),
		enabled,
	});

export const versionAttemptsListQueryOptions = ({
	versionId,
	enabled,
}: {
	versionId: string;
	enabled?: boolean;
}) =>
	queryOptions({
		queryKey: ["version-attempts", "list", versionId],
		queryFn: () => $listVersionAttempts({ data: { versionId } }),
		enabled,
	});

export const publicVersionsInfiniteQueryOptions = ({
	pageSize = 20,
}: {
	pageSize?: number;
} = {}) =>
	createInfiniteQueryOptions({
		queryKey: ["versions", "public", { pageSize }],
		queryFn: ({ pageParam }) =>
			$listPublicVersions({ data: { page: pageParam ?? 1, pageSize } }),
	});

export const userMistakesInfiniteQueryOptions = ({
	pageSize = 20,
	keyword,
	blockType,
	enabled,
}: {
	pageSize?: number;
	keyword?: string;
	blockType?: string;
	enabled?: boolean;
} = {}) =>
	createInfiniteQueryOptions({
		queryKey: ["mistakes", "list", { pageSize, keyword, blockType }],
		queryFn: ({ pageParam }) =>
			$searchUserMistakes({
				data: { page: pageParam ?? 1, pageSize, keyword: keyword || undefined, blockType },
			}),
		enabled,
		staleTime: 5 * 1000 * 60,
	});
